import { useMutation } from '@tanstack/react-query';
import { useContext } from 'react';

import { CartContext } from '@/contexts/CartContext';
import { TmCart } from '@/tscapi/model/cart';
import { TmFavouriteList } from '@/tscapi/model/favourite';
import { TmAccountRequest } from '@/tscapi/model/request/account-request';
import { TmMergeCartRequest } from '@/tscapi/model/request/account_cart-request';
import { getAccount } from '@/tscapi/service/account.service';
import { mergeCart, getAccountCart } from '@/tscapi/service/accountcart.service';
import { getFavourites } from '@/tscapi/service/favourite.service';
import { Storage } from '@/tscapi/utils/browser-storage';
import { Cookie } from '@/tscapi/utils/cookie';
import { FavouriteStorage } from '@/tscapi/utils/favourites-storage';

import { SigninFormType } from '../validation';

export function useSignin() {
    const { customerSignIn } = useContext(CartContext);

    return useMutation({
        mutationFn: async (data: SigninFormType & { recaptchaToken: string }) => {
            try {
                const signInRequest = new TmAccountRequest();
                signInRequest.Email = data.email;
                signInRequest.Password = data.password;
                signInRequest.ReCaptchaToken = data.recaptchaToken;

                const result = await customerSignIn(signInRequest);

                const cookie = new Cookie();
                if (
                    cookie.UserAccessToken != undefined &&
                    cookie.UserAccessToken.Edp > 0 &&
                    cookie.UserAccessToken.Token.length > 0
                ) {
                    try {
                        const [cartResult, favouritesResult] = await Promise.all([
                            processCart(cookie),
                            processFavourites(),
                        ]);

                        if (cartResult != null) {
                            processAccountCart(cartResult, cookie);
                        } else {
                            await processAccountData(cookie);
                        }

                        if (favouritesResult) {
                            const favouriteStorage = new FavouriteStorage();
                            favouriteStorage.Favourites = favouritesResult.Favourites.map(({ ItemNumber }) => ({
                                ItemNumber,
                            }));
                        }

                        return { success: true, result };
                    } catch (error) {
                        console.error('Error processing cart/favourites:', error);
                        try {
                            await processAccountData(cookie);
                        } catch (accountError) {
                            console.error('Error processing account data:', accountError);
                        }
                        return { success: true, result };
                    }
                } else {
                    if (result === 'too_many_requests') {
                        throw new Error(
                            "Sorry you are having trouble. Please click or tap on the 'Forgot password' link below to reset your password.",
                        );
                    } else if (result === 'account_inactive') {
                        throw new Error(
                            "Due to inactivity, your password needs to be reset to keep your account secure. Please click or tap on the 'Forgot password' link below to proceed.",
                        );
                    } else if (result === 'account_closed') {
                        throw new Error(
                            'Unfortunately your account is unavailable. Please contact our Customer Care Centre at 1-888-2020-888 for more information.',
                        );
                    } else {
                        throw new Error(
                            "We couldn't sign you in. Please check your email and password, then try again.",
                        );
                    }
                }
            } catch (error) {
                console.log({ error });
                throw error;
            }
        },
    });
}

async function processCart(cookie: Cookie): Promise<TmCart | null> {
    try {
        const cartReq = new TmMergeCartRequest();
        cartReq.CartGuid = cookie.CartGuid;

        const accountCart = cookie.CartGuid ? await mergeCart(cartReq) : await getAccountCart();
        return accountCart?.data || null;
    } catch (error) {
        console.error('Error processing cart:', error);
        return null;
    }
}

async function processFavourites(): Promise<TmFavouriteList | null> {
    try {
        const favouriteStorage = new FavouriteStorage();
        favouriteStorage.clear();

        if (favouriteStorage.Favourites.length === 0) {
            return await getFavourites();
        }
        return null;
    } catch (error) {
        console.error('Error processing favourites:', error);
        return null;
    }
}

function processAccountCart(acctCart: TmCart, cookie: Cookie): void {
    if (!acctCart) return;

    const { BillingAddress } = acctCart.Buyer;
    cookie.UserName = BillingAddress.FirstName;
    cookie.LastName = BillingAddress.LastName;
    cookie.CartGuid = acctCart.CartGuid;
    cookie.CartMerge = acctCart.IsCartMerged.toString();
    cookie.CartProductCount = acctCart.OrderSummary.TotalQuantity.toString();

    new Storage().setSessionCart(acctCart);
    (window as any).analyticsData.cart = acctCart;
}

async function processAccountData(cookie: Cookie): Promise<void> {
    const accountData = await getAccount();
    const { BillingAddress } = accountData?.data || {};

    if (BillingAddress) {
        cookie.UserName = BillingAddress.FirstName;
        cookie.LastName = BillingAddress.LastName;
    }
}
